import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Calendar, Clock, ChevronLeft, ChevronRight, Check, MessageSquare, Building, User } from 'lucide-react';
import { Avatar } from '@clareefai/ui';
import { useData } from '../../hooks/useData';
import { Prospect } from '@clareefai/types';

interface ProspectMeetingSchedulerModalProps {
  prospect: Prospect;
  isOpen: boolean;
  onClose: () => void;
}

const ProspectMeetingSchedulerModal: React.FC<ProspectMeetingSchedulerModalProps> = ({ prospect, isOpen, onClose }) => {
  const { t } = useTranslation();
  const { addMeeting, promoters } = useData();
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedPromoter, setSelectedPromoter] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [showNotesSection, setShowNotesSection] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Heures disponibles
  const availableHours = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00'
  ];

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
      document.body.style.overflow = 'hidden';
    } else if (shouldRender) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
        document.body.style.overflow = 'unset';
      }, 300);
      return () => clearTimeout(timer);
    }
    
    return () => {
      if (!isOpen) {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isOpen, shouldRender]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      // Reset form
      setSelectedDate(null);
      setSelectedTime(null);
      setSelectedPromoter(null);
      setNotes('');
      setShowSuccess(false);
    }, 100);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!shouldRender) return null;

  // Obtenir les promoteurs associés au prospect
  const associatedPromoters = promoters.filter(p => 
    prospect.assignedPromoters?.includes(p.id)
  );

  // Fonctions du calendrier
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1; // Lundi = 0
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPastDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isSameDate = (date1: Date, date2: Date) => {
    return date1.toDateString() === date2.toDateString();
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(currentDate.getMonth() - 1);
    } else {
      newDate.setMonth(currentDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleDateSelect = (day: number) => {
    const selectedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    if (!isPastDate(selectedDate)) {
      setSelectedDate(selectedDate);
      setSelectedTime(null); // Reset time selection
    }
  };

  const handleScheduleMeeting = async () => {
    if (!selectedDate || !selectedTime || !selectedPromoter) {
      return;
    }

    setIsScheduling(true);

    // Simuler l'API call
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Créer le nouveau meeting avec le prospect et le promoteur associé
    const newMeeting = {
      prospectName: prospect.name,
      promoterName: selectedPromoter,
      date: selectedDate.toISOString().split('T')[0],
      time: selectedTime,
      email: prospect.email,
      status: 'booked' as const
    };

    addMeeting(newMeeting);
    setIsScheduling(false);
    setShowSuccess(true);

    // Fermer le modal après 2 secondes
    setTimeout(() => {
      handleClose();
    }, 2000);
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Jours vides au début
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-10"></div>);
    }

    // Jours du mois
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const isSelected = selectedDate && isSameDate(date, selectedDate);
      const isPast = isPastDate(date);
      const isTodayDate = isToday(date);

      days.push(
        <button
          key={day}
          onClick={() => handleDateSelect(day)}
          disabled={isPast}
          className={`h-10 w-10 rounded-lg text-sm font-medium transition-all duration-200 ${
            isSelected
              ? 'bg-green-600 text-white shadow-lg scale-110'
              : isPast
              ? 'text-gray-300 cursor-not-allowed'
              : isTodayDate
              ? 'bg-blue-100 text-blue-800 border-2 border-blue-400 hover:bg-blue-200'
              : 'text-gray-700 hover:bg-green-100 hover:text-green-700'
          }`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  if (showSuccess) {
    return (
      <>
        <div className={`modal-overlay ${isOpen && !isClosing ? 'open' : ''} ${isClosing ? 'closing' : ''}`} />
        <div className={`slide-modal ${isOpen && !isClosing ? 'open' : ''} ${isClosing ? 'closing' : ''}`}>
          <div className="flex items-center justify-center min-h-full p-6">
            <div className="bg-white rounded-xl p-8 text-center max-w-md w-full">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t('prospects.scheduler.scheduledSuccess')}
              </h3>
              <p className="text-gray-600 mb-6">
                {t('prospects.scheduler.scheduledDetails', { prospect: prospect.name, promoter: selectedPromoter, date: selectedDate?.toLocaleDateString('en-US'), time: selectedTime })}
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  {t('prospects.scheduler.dashboardAppears')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div 
        className={`modal-overlay ${isOpen && !isClosing ? 'open' : ''} ${isClosing ? 'closing' : ''}`} 
        onClick={handleOverlayClick} 
      />
      <div className={`slide-modal ${isOpen && !isClosing ? 'open' : ''} ${isClosing ? 'closing' : ''}`} style={{ width: '900px', maxWidth: '95vw' }}>
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white modal-content">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('prospects.scheduler.title')}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        <div className="flex-1 p-6 overflow-y-auto bg-white modal-content">
          {/* Informations du prospect */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 mb-6 border border-green-200">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{prospect.name}</h3>
                <p className="text-sm text-gray-600 flex items-center">
                  <Building className="w-4 h-4 mr-1" />
                  {prospect.company}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {t('prospects.scheduler.promoterLabel')} {prospect.promoterName}
                </p>
                <p className="text-xs text-gray-500">
                  {t('prospects.scheduler.availablePromoters')} {associatedPromoters.length}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* Sélection du promoteur */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <User className="w-5 h-5 mr-2 text-purple-600" />
                {t('prospects.scheduler.choosePromoter')}
              </h3>
              
              <div className="space-y-3">
                {associatedPromoters.map((promoter) => (
                  <button
                    key={promoter.id}
                    onClick={() => setSelectedPromoter(promoter.name)}
                    className={`w-full p-3 text-left rounded-lg border-2 transition-all duration-200 ${
                      selectedPromoter === promoter.name
                        ? 'bg-purple-600 text-white border-purple-600 shadow-lg'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-purple-400 hover:bg-purple-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Avatar
                        src={promoter.avatar}
                        name={promoter.name}
                        size="md"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium truncate">{promoter.name}</h4>
                        <p className={`text-sm truncate ${
                          selectedPromoter === promoter.name ? 'text-purple-200' : 'text-gray-600'
                        }`}>
                          {promoter.company}
                        </p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`text-xs ${
                            selectedPromoter === promoter.name ? 'text-purple-200' : 'text-gray-500'
                          }`}>
                            {promoter.prospectsReferred} {t('prospects.prospectsCount').toLowerCase()}
                          </span>
                          <span className={`text-xs ${
                            selectedPromoter === promoter.name ? 'text-purple-200' : 'text-gray-500'
                          }`}>
                            {promoter.conversionRate}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {selectedPromoter && (
                <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-sm text-purple-800 font-medium">
                    {t('prospects.scheduler.selectedPromoter')} {selectedPromoter}
                  </p>
                </div>
              )}
            </div>

            {/* Calendrier */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-green-600" />
                {t('prospects.scheduler.selectDate')}
              </h3>
              
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                {/* Navigation du mois */}
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => navigateMonth('prev')}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    disabled={!selectedPromoter}
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-600" />
                  </button>
                  <h4 className="text-lg font-semibold text-gray-900">
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h4>
                  <button
                    onClick={() => navigateMonth('next')}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    disabled={!selectedPromoter}
                  >
                    <ChevronRight className="w-5 h-5 text-gray-600" />
                  </button>
                </div>

                {!selectedPromoter && (
                  <div className="text-center py-8 text-gray-500">
                    <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">
                      {t('prospects.scheduler.selectPromoterFirst')}
                    </p>
                  </div>
                )}

                <>
                  {/* Jours de la semaine */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {weekDays.map((day) => (
                      <div key={day} className="h-8 flex items-center justify-center text-xs font-medium text-gray-500">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Grille du calendrier */}
                  <div className="grid grid-cols-7 gap-1">
                    {renderCalendar()}
                  </div>
                </>

                {selectedDate && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800 font-medium">
                      {t('prospects.scheduler.selectedDate')} {' '}
                      {selectedDate.toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Sélection d'heure */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-blue-600" />
                {t('prospects.scheduler.selectTime')}
              </h3>

              {!selectedDate ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">
                    {t('prospects.scheduler.selectDateFirst')}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {availableHours.map((hour) => (
                    <button
                      key={hour}
                      onClick={() => setSelectedTime(hour)}
                      className={`p-3 text-sm font-medium rounded-lg border-2 transition-all duration-200 ${
                        selectedTime === hour
                          ? 'bg-blue-600 text-white border-blue-600 shadow-lg scale-105'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                      }`}
                    >
                      {hour}
                    </button>
                  ))}
                </div>
              )}

              {selectedTime && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium">
                    {t('prospects.scheduler.selectedTime')} {selectedTime}
                  </p>
                </div>
              )}

              {/* Bouton Ajouter note supplémentaire */}
              {selectedDate && selectedTime && !showNotesSection && (
                <div className="mt-6 text-center">
                  <button
                    onClick={() => setShowNotesSection(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm"
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span>{t('prospects.scheduler.addNote')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Notes supplémentaires - Affichées seulement si demandées */}
          {showNotesSection && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MessageSquare className="w-5 h-5 mr-2 text-orange-600" />
              {t('prospects.scheduler.additionalNotes')}
            </h3>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('prospects.scheduler.addNoteFor')}
                </label>
                <button
                  onClick={() => {
                    setShowNotesSection(false);
                    setNotes('');
                  }}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  {t('prospects.scheduler.remove')}
                </button>
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={6}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                placeholder={t('prospects.scheduler.notesPlaceholder')}
              />
            </div>
          </div>
          )}

          {/* Récapitulatif et bouton de programmation */}
          {selectedDate && selectedTime && selectedPromoter && (
            <div className="mb-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
              <h4 className="text-lg font-semibold text-green-900 mb-4">
                {t('prospects.scheduler.summary')}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p><strong>{t('prospects.scheduler.prospect')}</strong> {prospect.name}</p>
                  <p><strong>{t('prospects.scheduler.companyLabel')}</strong> {prospect.company}</p>
                  <p><strong>{t('prospects.scheduler.emailLabel')}</strong> {prospect.email}</p>
                </div>
                <div>
                  <p><strong>{t('prospects.scheduler.dateLabel')}</strong> {selectedDate.toLocaleDateString('en-US')}</p>
                  <p><strong>{t('prospects.scheduler.timeLabel')}</strong> {selectedTime}</p>
                  <p><strong>{t('prospects.scheduler.promoterValue')}</strong> {selectedPromoter}</p>
                </div>
              </div>
              {notes && (
                <div className="mt-4 pt-4 border-t border-green-200">
                  <p className="text-sm"><strong>{t('prospects.scheduler.notesLabel')}</strong></p>
                  <p className="text-sm text-green-800 mt-1 italic">"{notes}"</p>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between pt-6 border-t border-gray-200">
            <button
              onClick={handleClose}
              className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
            >
              {t('common.cancel')}
            </button>

            <button
              onClick={handleScheduleMeeting}
              disabled={!selectedDate || !selectedTime || !selectedPromoter || isScheduling}
              className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isScheduling ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>{t('prospects.scheduler.scheduling')}</span>
                </>
              ) : (
                <>
                  <Calendar className="w-5 h-5" />
                  <span>{t('prospects.scheduler.scheduleButton')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProspectMeetingSchedulerModal;